import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articlesData, articleList } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const SITE_URL = typeof __VITE_SITE_URL__ !== "undefined" ? __VITE_SITE_URL__ : "https://kakobuypolska.top";

const parsePolishDate = (dateStr: string): string => {
  const months: Record<string, string> = {
    'stycznia': '01', 'lutego': '02', 'marca': '03', 'kwietnia': '04',
    'maja': '05', 'czerwca': '06', 'lipca': '07', 'sierpnia': '08',
    'września': '09', 'października': '10', 'listopada': '11', 'grudnia': '12',
  };
  const parts = dateStr.split(' ');
  if (parts.length !== 3) return '2026-07-20';
  const day = parts[0].padStart(2, '0');
  const month = months[parts[1]] || '07';
  const year = parts[2];
  return `${year}-${month}-${day}`;
};

export interface ArticleKeyword {
  keyword: string;
  count: number;
  density: string;
}

export interface ArticleSection {
  title: string;
  paragraphs: string[];
}

export interface ArticleData {
  slug: string;
  title: string;
  date: string;
  author: string;
  image: string;
  excerpt: string;
  keywords: ArticleKeyword[];
  sections: ArticleSection[];
}

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? articlesData[slug] : undefined;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 px-6 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">
            Artykuł nie został znaleziony
          </h1>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 underline">
            ← {t("article_breadcrumb_home")}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Related articles (exclude current)
  const relatedArticles = articleList
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Schema.org JSON-LD */}
      {article && (
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                headline: article.title,
                description: article.excerpt,
                image: article.image,
                datePublished: parsePolishDate(article.date),
                dateModified: "2026-07-20",
                author: {
                  "@type": "Organization",
                  name: article.author,
                  url: `${SITE_URL}/`,
                },
                publisher: {
                  "@type": "Organization",
                  name: "Kakobuy Spreadsheet Polska",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
                  },
                },
                mainEntityOfPage: {
                  "@type": "WebPage",
                  "@id": `${SITE_URL}/artykul/${article.slug}`,
                },
                inLanguage: "pl-PL",
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Strona Główna",
                    item: `${SITE_URL}/`,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: article.title,
                    item: `${SITE_URL}/artykul/${article.slug}`,
                  },
                ],
              }),
            }}
          />
        </>
      )}

      <article className="pt-24 md:pt-28 pb-20 px-6 md:px-10">
        <div className="max-w-[900px] mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              {t("article_breadcrumb_home")}
            </Link>
            <span>/</span>
            <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-[1.15] mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.author}</span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="aspect-[2/1] rounded-2xl overflow-hidden mb-12 bg-background-200">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content Sections */}
          {article.sections.map((section, idx) => (
            <section key={idx} className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
                {section.title}
              </h2>
              {section.paragraphs.map((para, pIdx) => (
                <p key={pIdx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4 last:mb-0">
                  {para}
                </p>
              ))}
            </section>
          ))}

          {/* Keyword Density Analysis Table */}
          <section className="mb-12 p-6 md:p-8 rounded-2xl bg-background-100">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <i className="ri-bar-chart-2-line text-accent-500"></i>
              {t("article_keyword_table_title")}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      {t("article_keyword_col_keyword")}
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      {t("article_keyword_col_count")}
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      {t("article_keyword_col_density")}
                    </th>
                    <th className="hidden sm:table-cell py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      Dystrybucja
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {article.keywords.map((kw, idx) => {
                    const maxCount = Math.max(...article.keywords.map((k) => k.count));
                    const barWidth = (kw.count / maxCount) * 100;
                    return (
                      <tr key={idx} className="border-b border-background-200/30 last:border-none">
                        <td className="py-3 px-4 font-medium text-foreground-800">
                          {kw.keyword}
                        </td>
                        <td className="text-center py-3 px-4 text-foreground-600 font-mono text-xs">
                          {kw.count}
                        </td>
                        <td className="text-center py-3 px-4 text-foreground-600 font-mono text-xs">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            parseFloat(kw.density) >= 2.5 ? "bg-accent-100 text-accent-700" :
                            parseFloat(kw.density) >= 1.5 ? "bg-primary-100 text-primary-700" :
                            "bg-background-200 text-foreground-600"
                          }`}>
                            {kw.density}
                          </span>
                        </td>
                        <td className="hidden sm:table-cell py-3 px-4">
                          <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-accent-400 transition-all duration-500"
                              style={{ width: `${barWidth}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Categories Quick Links */}
          <section className="mb-12">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
              Kategorie Kakobuy Spreadsheet
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 p-3 rounded-xl bg-background-100 hover:bg-accent-100/60 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className="ri-arrow-right-up-line text-accent-600 text-sm"></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800">{t(cat.nameKey)}</span>
                </a>
              ))}
            </div>
          </section>

          {/* Article CTA */}
          <div className="text-center py-12 px-6 rounded-2xl bg-accent-50 mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
              {t("article_cta_title")}
            </h2>
            <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
              {t("article_cta_desc")}
            </p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("article_cta_button")}
            </Link>
          </div>

          {/* Related Links */}
          <div className="border-t border-background-200/70 pt-10">
            <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">
              {t("article_related_title")}
            </h3>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/"
                className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-accent-100/60 transition-colors whitespace-nowrap"
              >
                {t("article_related_link1")}
              </Link>
              {categories.slice(0, 6).map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-accent-100/60 transition-colors whitespace-nowrap"
                >
                  {t(cat.nameKey)}
                </a>
              ))}
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-10 pt-10 border-t border-background-200/70">
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-5">
                Inne Artykuły, Które Mogą Cię Zainteresować
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/artykul/${ra.slug}`}
                    className="group p-4 rounded-xl bg-background-100 hover:bg-accent-100/60 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/9] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 leading-snug mb-1 line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-1">
                      {ra.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}